/**
 * UranIA PA — OIDC UserManager (oidc-client-ts) — US-009.
 *
 * Flusso: Authorization Code + PKCE (analisi §16.4) contro Keycloak
 * `urania-pa-staging` (vedi US-004 SPID, US-005 CIE, US-006 CNS).
 *
 * Boundary di sicurezza:
 *   - `userStore` e `stateStore` sono `WebStorageStateStore` con backing
 *     `inMemoryStorage` (vedi `in-memory-storage.ts`) → niente token in
 *     localStorage/sessionStorage; il refresh avviene via silent renew
 *     iframe (Keycloak SSO cookie HttpOnly fa il refresh).
 *   - `scope: 'openid profile email'` — non si richiede `offline_access`:
 *     niente refresh_token grant restituito al client (analisi §16.4 +
 *     US-009 AC#5 "refresh token in HttpOnly cookie" mappato sul cookie
 *     SSO di Keycloak).
 *   - `response_type: 'code'` esplicito; PKCE S256 automatico via
 *     oidc-client-ts.
 *   - `automaticSilentRenew: true` + `silent_redirect_uri` per renew sotto
 *     traccia trasparente all'utente.
 *
 * NB: il `UserManager` viene istanziato lazy alla prima richiesta — niente
 * side-effect a import-time, così lo scheleton SPA (US-009) può montare
 * pagine non protette senza forzare un round-trip OIDC.
 */

import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';

import { loadSpaEnv } from '../env.ts';
import { inMemoryStorage } from './in-memory-storage.ts';

let cachedManager: UserManager | null = null;

export function buildUserManagerSettings(): UserManagerSettings {
  const env = loadSpaEnv();
  return {
    authority: env.oidcAuthority,
    client_id: env.oidcClientId,
    redirect_uri: env.oidcRedirectUri,
    post_logout_redirect_uri: env.oidcPostLogoutRedirectUri,
    silent_redirect_uri: env.oidcSilentRedirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    loadUserInfo: false,
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: false,
    revokeTokensOnSignout: true,
    userStore: new WebStorageStateStore({ store: inMemoryStorage }),
    // stateStore: PKCE code_verifier + state + nonce devono sopravvivere al
    // full-page redirect verso Keycloak. inMemoryStorage si azzera al
    // navigate, quindi usiamo sessionStorage (per-tab, scope ridotto).
    // I token (userStore) restano in-memory per non esporli a XSS.
    stateStore: new WebStorageStateStore({
      store: typeof window !== 'undefined' ? window.sessionStorage : inMemoryStorage,
    }),
    // PKCE è on di default in oidc-client-ts v3; lo esplicitiamo per safety.
    // (cf. https://authts.github.io/oidc-client-ts/ §UserManagerSettings)
  };
}

export function getUserManager(): UserManager {
  cachedManager ??= new UserManager(buildUserManagerSettings());
  return cachedManager;
}

/**
 * Test-only: resetta il singleton (NON esportato per consumo applicativo —
 * solo per unit test futuri di US-010+).
 */
export function __resetUserManagerForTests(): void {
  cachedManager = null;
}
